import type { RadioGroupProps } from '@headlessui/react';
import {
  Radio as HeadlessUiRadio,
  RadioGroup as HeadlessUiRadioGroup,
} from '@headlessui/react';
import cn from 'classnames';
import React, { forwardRef, Fragment } from 'react';

import { Error } from 'components/UI/Tailwind/Error';
import { Label } from 'components/UI/Tailwind/Label';

type TRef = React.ForwardedRef<HTMLElement>;

type TInput = JSX.IntrinsicElements['input'];

type RadioGroup<TTag extends React.ElementType = 'div', TType = string> = Pick<
  TInput,
  'disabled'
> &
  RadioGroupProps<TTag, TType> &
  Pick<Label, 'label' | 'labelSuffix' | 'required'> & {
    error?: string;
    groupClassName?: string;
    hint?: React.ReactNode;
    inline?: boolean;
    options: Array<{
      badge?: number;
      disabled?: boolean;
      /** Not Headless-disabled: keeps full opacity and hover; selection is blocked in `onChange`. */
      locked?: boolean;
      label?:
        | string
        | React.ReactElement
        | (({ value }: { value?: TType }) => React.ReactElement | null);
      value?: TType;
    }>;
    size?: 20 | 32;
    variant?:
      | 'buttons'
      | 'picker'
      | 'radio'
      | 'selectors'
      | 'toggle'
      | 'unstyled';
  };

function RadioGroupInner<
  TTag extends React.ElementType = 'div',
  TType = string,
>(
  {
    disabled: _disabled,
    error,
    groupClassName,
    hint,
    inline = false,
    label,
    labelSuffix,
    options,
    required,
    size = 32,
    variant = 'toggle',
    ...headlessUiRadioGroupProps
  }: RadioGroup<TTag, TType>,
  ref: TRef,
) {
  const { onChange: onChangeFromProps, ...restHeadlessProps } =
    headlessUiRadioGroupProps;

  return (
    <div>
      <Label label={label} labelSuffix={labelSuffix} required={required} />
      <HeadlessUiRadioGroup
        {...restHeadlessProps}
        onChange={(value) => {
          const chosen = options.find((o) => o.value === value);
          if (chosen?.locked) {
            return;
          }
          onChangeFromProps?.(value);
        }}
        ref={ref}
        className={cn(
          'select-none',
          {
            'border-red-600': variant === 'toggle' && error,
            'cursor-not-allowed': _disabled,
            'flex flex-col gap-y-2': variant === 'radio' && !inline,
            'flex flex-wrap items-center gap-x-4 gap-y-2':
              variant === 'radio' && inline,
            'gap-x-2': variant === 'buttons' || variant === 'selectors',
            'grid grid-cols-1 gap-2 md:grid-cols-2': variant === 'picker',
            'inline-flex items-center': variant !== 'radio',
            'rounded-[16px] border border-gray-300 bg-gray-300 p-[1px]':
              (variant === 'toggle' && size === 32) ||
              (variant === 'toggle' && size === 20),
          },
          groupClassName,
        )}
      >
        {options.map((option, index) => {
          const radioDisabled =
            _disabled || (!!option.disabled && !option.locked);
          return (
            <HeadlessUiRadio
              key={index}
              as={Fragment}
              disabled={radioDisabled}
              value={option.value}
            >
              {({ checked }) => (
                <button
                  aria-disabled={option.locked && !radioDisabled}
                  className={cn(
                    'group flex shrink-0 gap-x-2 transition-colors duration-200',
                    {
                      'items-center': variant !== 'radio' || inline,
                      'items-start': variant === 'radio' && !inline,
                      'bg-blue-500/8 text-blue-500':
                        variant === 'selectors' && checked,
                      'bg-transparent text-gray-600 hover:bg-black/4 active:bg-black/8':
                        variant === 'selectors' && !checked,
                      'bg-white text-gray-800': variant === 'toggle' && checked,
                      'border-blue-500 bg-blue-500/8 text-blue-500 hover:bg-blue-500/12 focus:bg-blue-500/16 active:bg-blue-500/16':
                        variant === 'buttons' && checked,
                      'border-gray-400 bg-white text-gray-600 hover:bg-gray-100 active:bg-gray-200':
                        variant === 'buttons' && !checked,
                      'cursor-not-allowed opacity-50': radioDisabled,
                      'cursor-default':
                        Boolean(option.locked) && !radioDisabled,
                      'cursor-pointer': !radioDisabled && !option.locked,
                      'min-h-5':
                        size === 20 &&
                        variant !== 'unstyled' &&
                        variant !== 'picker',
                      'min-h-8':
                        size === 32 &&
                        variant !== 'unstyled' &&
                        variant !== 'picker',
                      'rounded-[16px] px-2.5 text-10/100% font-medium':
                        variant === 'toggle' && size === 20,
                      'rounded-[16px] px-4 text-14/100% font-medium':
                        variant === 'toggle' && size === 32,
                      'rounded-[8px] border px-4 text-14/100% font-semibold':
                        variant === 'buttons',
                      'rounded-[8px] px-3 text-14/100% font-medium':
                        variant === 'selectors',
                      'text-14/120% font-medium': variant === 'radio',
                      'text-gray-500': variant === 'toggle' && !checked,
                    },
                  )}
                  type="button"
                >
                  {variant === 'radio' && (
                    <div
                      className={cn(
                        'flex h-5 w-5 items-center justify-center rounded-full border',
                        {
                          'border-gray-400 hover:bg-gray-100 group-hover:bg-gray-100':
                            !checked && !radioDisabled,
                          'border-blue-500': checked && !radioDisabled,
                        },
                      )}
                    >
                      {checked && (
                        <div
                          className={cn('h-2.5 w-2.5 rounded-full', {
                            'bg-blue-500': !radioDisabled,
                            'bg-gray-500': radioDisabled,
                          })}
                        />
                      )}
                    </div>
                  )}
                  {typeof option.label === 'function'
                    ? option.label({ value: option.value })
                    : option.label}
                  {option.badge !== undefined && (
                    <span
                      className={cn({
                        'text-blue-500/64': variant === 'selectors' && checked,
                        'text-gray-500': variant === 'selectors' && !checked,
                      })}
                    >
                      {option.badge}
                    </span>
                  )}
                </button>
              )}
            </HeadlessUiRadio>
          );
        })}
      </HeadlessUiRadioGroup>
      {hint && (
        <span className="block pt-2 text-14/120% text-gray-500">{hint}</span>
      )}
      <Error>{error}</Error>
    </div>
  );
}

export const RadioGroup = forwardRef(RadioGroupInner) as <
  TTag extends React.ElementType = 'div',
  TType = string,
>(
  props: RadioGroup<TTag, TType> & { ref?: TRef },
) => ReturnType<typeof RadioGroupInner>;
